import { useEffect, useState } from "react";
import { Link } from "../components/LocaleLink";
import { ArrowRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { fetchProperties } from "../lib/api";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { JsonLd, buildFaqSchema } from "../components/JsonLd";
import { PropertyCard } from "../components/PropertyCard";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";

export default function Properties() {
  const { t } = useLang();
  const [filter, setFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = filter === "all" ? {} : { listing_type: filter };
    fetchProperties(params)
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [filter]);

  const filters = [
    { key: "all", label: t("properties.all") },
    { key: "sale", label: t("properties.sale") },
    { key: "rental", label: t("properties.rental") },
    { key: "mid_term", label: t("properties.midTerm") },
  ];

  return (
    <div data-testid="properties-page" className="pt-36 md:pt-44 pb-24 md:pb-32">
      <Seo title={`${t("properties.title")} | Velmira Living`} description={t("properties.subtitle")} />
      <JsonLd id="listings-faq" data={buildFaqSchema(t("listings.faq"))} />
      <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-[1600px]">
        <Reveal>
          <p lang="en" className="brand-caps font-accent text-xs uppercase tracking-[0.25em] text-primary">Velmira Living</p>
          <h1 className="mt-5 font-headings text-5xl sm:text-6xl leading-[1.05] tracking-tight text-foreground">{t("properties.title")}</h1>
          <p className="mt-5 font-body font-light text-lg text-foreground/70 max-w-2xl">{t("properties.subtitle")}</p>
          <p className="mt-6 font-body font-light text-base text-foreground/60 max-w-3xl leading-relaxed">{t("listings.seoIntro")}</p>
        </Reveal>

        <div className="mt-12 flex flex-wrap gap-3" data-testid="property-filters">
          {filters.map((f) => (
            <button
              key={f.key}
              data-testid={`filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`px-6 py-3 uppercase tracking-[0.2em] text-xs font-body border transition-all duration-300 ${
                filter === f.key
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-border/60 text-foreground/70 hover:border-primary hover:text-primary"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="properties-grid">
          {loading
            ? Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="aspect-square bg-secondary animate-pulse" />
              ))
            : items.map((p, i) => (
                <Reveal key={p.id} delay={(i % 3) * 0.08}><PropertyCard property={p} /></Reveal>
              ))}
        </div>

        {!loading && items.length === 0 && (
          <p data-testid="properties-empty" className="mt-16 font-body font-light text-foreground/60 text-center">{t("properties.empty")}</p>
        )}

        {/* Valuation / consultation CTA */}
        <Reveal>
          <div data-testid="valuation-cta" className="mt-24 bg-secondary border border-border/60 p-10 md:p-16 flex flex-col md:flex-row md:items-center justify-between gap-8">
            <div className="max-w-2xl">
              <h2 className="font-headings text-3xl sm:text-4xl leading-tight text-foreground">{t("properties.valuationTitle")}</h2>
              <p className="mt-4 font-body font-light text-foreground/70 leading-relaxed">{t("properties.valuationText")}</p>
            </div>
            <Link to="/contact?interest=valuation" data-testid="valuation-cta-button" className="inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 px-9 py-4 uppercase tracking-[0.2em] text-xs font-body transition-all duration-300 whitespace-nowrap self-start">
              {t("properties.valuationBtn")} <ArrowRight size={15} />
            </Link>
          </div>
        </Reveal>

        {/* Area guide (SEO content) */}
        <Reveal>
          <div data-testid="area-guide" className="mt-24 max-w-3xl">
            <h2 className="font-headings text-3xl sm:text-4xl leading-tight text-foreground">{t("listings.areaTitle")}</h2>
            <div className="mt-6 space-y-5 font-body font-light text-foreground/70 leading-relaxed">
              {t("listings.areaGuide").map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          </div>
        </Reveal>

        {/* FAQ (SEO content) */}
        <Reveal>
          <div className="mt-20 max-w-3xl">
            <h2 className="font-headings text-3xl sm:text-4xl leading-tight text-foreground mb-8">{t("listings.faqTitle")}</h2>
            <Accordion type="single" collapsible className="w-full" data-testid="listings-faq">
              {t("listings.faq").map((f, i) => (
                <AccordionItem key={i} value={`lfaq-${i}`} className="border-border/60">
                  <AccordionTrigger data-testid={`listings-faq-trigger-${i}`} className="font-headings text-xl md:text-2xl text-foreground hover:text-primary hover:no-underline text-left py-6">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="font-body font-light text-foreground/70 leading-relaxed text-base pb-6">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
